// War Ledger hologram (GDD §6, ART-DIRECTION §1 cyberpunk terminal): a receipt painted on a Canvas 2D by one function
// of time, paint(model, t), in the motion-designer style (hud/motion.ts). On opening, the sheet unfolds on a spring
// behind a scan line and the title types in. The rows slide in on staggered springs and the figures roll up like an
// odometer. The spend bar grows segment by segment, the deploy chips pop, and a PAID BY TAXPAYERS stamp slams down.
// While it is open, a changed figure rolls to its new value and sweeps its row, and a deploy pops its chip. Closing
// folds the sheet away. The painter never reads a clock: the ledger passes t, and ∞ paints the settled state
// ("Reduce effects"). Motion stays local and soft; nothing flashes (ART-DIRECTION §4). Layout is in CSS px; colours
// and fonts come from the HUD theme's custom properties (theme.ts), read again after each settings change. Painting
// allocates only when a shown number changes.

import { UNIT_KEYS, type UnitKey } from '@taxwar/content'
import { UNIT_PRESENTATION } from './catalog'
import { HudError } from './errors'
import { formatMoney, formatWhole } from './format'
import { SPRINGS, progress, stepAt, type Track } from './motion'

// -------------------------------------------------------------------------------------------- model

/** The ledger's figures, each a Track rolling toward its latest value. */
export const FIGURE_KEYS = ['spentUnits', 'spentBuildings', 'spentStrikes', 'warSpend', 'lostValue', 'citiesCaptured', 'citiesLost', 'perCity'] as const
export type FigureKey = (typeof FIGURE_KEYS)[number]

/** One unit type's deploys, in UNIT_KEYS order. */
export interface DeployCell {
  readonly unit: UnitKey
  count: number
  readonly spent: Track
  /** Ledger time of the last deploy counted while the sheet was open (−∞: none). */
  poppedAt: number
}

/** The sheet's lifecycle on the ledger clock. `from` is how far the sheet was unfolded when closing began. */
export type SheetState =
  | { readonly kind: 'closed' }
  | { readonly kind: 'open'; readonly since: number }
  | { readonly kind: 'closing'; readonly since: number; readonly from: number }

export interface LedgerModel {
  readonly figures: Readonly<Record<FigureKey, Track>>
  /** Ledger time each figure last changed while the sheet was open (−∞: not since it opened). */
  readonly changedAt: Readonly<Record<FigureKey, number>>
  readonly deploys: readonly DeployCell[]
  readonly sheet: SheetState
  /** Ledger time the PAID stamp lands (∞ while nothing is billed). */
  readonly stampAt: number
}

// -------------------------------------------------------------------------------------------- copy

/** The ledger's words; the accessible text copy (ledger.ts) uses the same labels. */
export const LEDGER_COPY = {
  title: 'WAR LEDGER',
  closeHint: 'ESC',
  spend: 'War spending',
  units: 'Units',
  buildings: 'Buildings',
  strikes: 'Strikes',
  lost: 'Value lost in action',
  captured: 'Cities acquired',
  misplaced: 'Cities misplaced',
  perCity: 'Cost per city captured',
  noCaptures: 'none yet',
  deploys: 'Deploys by unit',
  stamp: 'PAID BY TAXPAYERS',
} as const

/** How a figure reads: money, a count, or a cost per city (pending until a city is captured). */
type FigureFormat = 'money' | 'count' | 'perCity'

const FIGURE_FORMATS: Readonly<Record<FigureKey, FigureFormat>> = {
  spentUnits: 'money',
  spentBuildings: 'money',
  spentStrikes: 'money',
  warSpend: 'money',
  lostValue: 'money',
  citiesCaptured: 'count',
  citiesLost: 'count',
  perCity: 'perCity',
}

/** Per format: the whole number a (rolling) value shows as, and its text. Money floors like formatMoney; counts round. */
interface FigureFormatter {
  readonly whole: (value: number) => number
  readonly text: (whole: number) => string
}

const FORMATTERS: Readonly<Record<FigureFormat, FigureFormatter>> = {
  money: { whole: Math.floor, text: formatMoney },
  count: { whole: Math.round, text: formatWhole },
  perCity: { whole: Math.floor, text: formatMoney },
}

/** Whether a figure has nothing to show yet: the cost per city before any city is captured. */
export function figurePending(key: FigureKey, model: Pick<LedgerModel, 'figures'>): boolean {
  return FIGURE_FORMATS[key] === 'perCity' && model.figures.citiesCaptured.target === 0
}

/** A figure's value as text (for a figure that is not pending). */
export function formatFigure(key: FigureKey, value: number): string {
  const format = FORMATTERS[FIGURE_FORMATS[key]]
  return format.text(format.whole(value))
}

type Tone = 'frame' | 'text' | 'textDim' | 'money' | 'warning' | 'player' | 'enemy'
type PipStyle = 'none' | 'solid' | 'hollow'

interface RowDef {
  readonly label: string
  readonly figure: FigureKey
  readonly tone: Tone
  readonly pips: PipStyle
}

/** The rows under the spend block, top to bottom. */
const ROWS: readonly RowDef[] = [
  { label: LEDGER_COPY.lost.toUpperCase(), figure: 'lostValue', tone: 'warning', pips: 'none' },
  { label: LEDGER_COPY.captured.toUpperCase(), figure: 'citiesCaptured', tone: 'player', pips: 'solid' },
  { label: LEDGER_COPY.misplaced.toUpperCase(), figure: 'citiesLost', tone: 'enemy', pips: 'hollow' },
  { label: LEDGER_COPY.perCity.toUpperCase(), figure: 'perCity', tone: 'money', pips: 'none' },
]

/** Spend composition: the bar's segments and the legend under it, in order. */
const SPEND_PARTS: readonly { readonly figure: FigureKey; readonly label: string; readonly tone: Tone }[] = [
  { figure: 'spentUnits', label: LEDGER_COPY.units.toUpperCase(), tone: 'player' },
  { figure: 'spentBuildings', label: LEDGER_COPY.buildings.toUpperCase(), tone: 'money' },
  { figure: 'spentStrikes', label: LEDGER_COPY.strikes.toUpperCase(), tone: 'warning' },
]

const SPEND_LABEL = LEDGER_COPY.spend.toUpperCase()
const DEPLOYS_LABEL = LEDGER_COPY.deploys.toUpperCase()
const NO_CAPTURES = LEDGER_COPY.noCaptures.toUpperCase()
const PLUS_ONE = '+1'
/** Every prefix of the title, so typing it in allocates nothing. */
const TITLE_PREFIXES: readonly string[] = Array.from({ length: LEDGER_COPY.title.length + 1 }, (_, n) => LEDGER_COPY.title.slice(0, n))

// -------------------------------------------------------------------------------------------- layout (CSS px)

export const LEDGER_WIDTH = 300
const PAD_X = 12
const INNER_WIDTH = LEDGER_WIDTH - 2 * PAD_X
const ROW_HEIGHT = 21
/** Text baseline below a row's top. */
const ROW_BASELINE = 15
const DIVIDER_GAP = 6
const DEPLOY_COLUMNS = 3
const DEPLOY_ROWS = Math.ceil(UNIT_KEYS.length / DEPLOY_COLUMNS)
const CHIP = { height: 30, gap: 6, inset: 7, codeY: 12, spentY: 25 } as const
const CHIP_WIDTH = (INNER_WIDTH - (DEPLOY_COLUMNS - 1) * CHIP.gap) / DEPLOY_COLUMNS
const LEGEND = { swatch: 6, textGap: 4, column: INNER_WIDTH / SPEND_PARTS.length } as const
const PIPS = { x: 158, step: 9, radius: 3.2, max: 8 } as const
const CLOSE = { size: 18, top: 6, crossInset: 5, crossWidth: 1.5, hintGap: 8 } as const
const BRACKET = { size: 10, width: 2 } as const
const SCANLINE_PERIOD_PX = 3
const GLOW_PX = 44

/** Every y position, top to bottom, computed once. */
const LAYOUT = (() => {
  const titleY = 19
  const headerDivider = 30
  const spendLabelY = headerDivider + 16
  const spendValueY = spendLabelY + 28
  const barY = spendValueY + 9
  const barHeight = 6
  const legendNameY = barY + barHeight + 13
  const legendAmountY = legendNameY + 13
  const spendDivider = legendAmountY + DIVIDER_GAP + 3
  const rowsTop = spendDivider + 2
  const rowsDivider = rowsTop + ROWS.length * ROW_HEIGHT + DIVIDER_GAP / 2
  const deploysLabelY = rowsDivider + 17
  const chipsTop = deploysLabelY + 8
  const height = chipsTop + DEPLOY_ROWS * CHIP.height + (DEPLOY_ROWS - 1) * CHIP.gap + PAD_X
  return { titleY, headerDivider, spendLabelY, spendValueY, barY, barHeight, legendNameY, legendAmountY, spendDivider, rowsTop, rowsDivider, deploysLabelY, chipsTop, height }
})()

export const LEDGER_HEIGHT = LAYOUT.height

/** The close control's hit box, in CSS px of the unscaled sheet. */
export const CLOSE_BOX = { x: LEDGER_WIDTH - PAD_X - CLOSE.size, y: CLOSE.top, size: CLOSE.size } as const

/** The PAID stamp: its box's right edge and centre line, padding round the text, tilt, and the ink ring it throws off. */
const STAMP = { right: LEDGER_WIDTH - PAD_X - 2, y: LAYOUT.spendLabelY + 14, padX: 8, height: 22, angle: -0.14, ringFrom: 18, ringTo: 54, letterSpacing: '1.5px' } as const

// -------------------------------------------------------------------------------------------- motion

/** Open choreography, the beat sheet: when each part enters, in seconds after the sheet starts to unfold. */
const BEATS = {
  titleTyping: 0.05,
  spend: 0.1,
  bar: 0.22,
  barStagger: 0.07,
  rows: 0.16,
  rowStagger: 0.05,
  deploysLabel: 0.3,
  chips: 0.34,
  chipStagger: 0.03,
  stamp: 0.78,
} as const
const TITLE_CHARS_PER_SEC = 60
/** Rows slide in from this far right; chips grow from this scale. */
const ROW_SLIDE_PX = 14
const CHIP_START_SCALE = 0.6
/** A live deploy bumps its chip to 1 + CHIP_BUMP and floats a "+1" up this far. */
const CHIP_BUMP = 0.18
const FLOATER_RISE_PX = 12
const FLOATER_SEC = 0.7
/** The scan line rides the sheet's edge while it moves, then fades out over this long. */
const SCAN_FADE_SEC = 0.3
/** A changed row's highlight sweeps across it in this long. */
const SWEEP_SEC = 0.55
const SWEEP_WIDTH_PX = 70
const STAMP_START_SCALE = 2.2
const STAMP_FADE_IN_SEC = 0.08
const STAMP_RING_SEC = 0.4

/** How long the open choreography runs: its last beat plus that beat's motion. */
export const OPEN_CHOREOGRAPHY_SEC = Math.max(
  BEATS.stamp + SPRINGS.pop.settle,
  BEATS.rows + (ROWS.length - 1) * BEATS.rowStagger + SPRINGS.roll.settle,
  BEATS.chips + (UNIT_KEYS.length - 1) * BEATS.chipStagger + SPRINGS.pop.settle,
  SPRINGS.snap.settle + SCAN_FADE_SEC,
)
/** When the PAID stamp lands after the sheet opens (with something already billed). */
export const OPEN_STAMP_DELAY_SEC = BEATS.stamp
/** How long a live change animates (a roll, a sweep, a chip pop and its floater). */
export const LIVE_CUE_SEC = Math.max(SWEEP_SEC, FLOATER_SEC, SPRINGS.pop.settle, SPRINGS.roll.settle)
/** How long the stamp takes to land. */
export const STAMP_SEC = Math.max(SPRINGS.pop.settle, STAMP_RING_SEC)
/** How long the sheet takes to fold away. */
export const FOLD_SEC = SPRINGS.fold.settle
/** How far the sheet is unfolded `openFor` seconds after it opened (it overshoots a little). */
export function unfoldAt(openFor: number): number {
  return stepAt(SPRINGS.snap, openFor)
}

/** Opacities (canvas globalAlpha) of the sheet's parts: a dim, translucent hologram. */
const ALPHA = {
  sheet: 0.9,
  glow: 0.12,
  scanlines: 0.05,
  border: 0.25,
  bracket: 0.6,
  divider: 0.2,
  track: 0.14,
  chipIdle: 0.45,
  chipBorder: 0.5,
  chipBorderIdle: 0.18,
  close: 0.5,
  sweep: 0.16,
  stamp: 0.9,
  stampRing: 0.35,
  scanLine: 0.85,
} as const

const HALF = 0.5
const FULL_TURN = Math.PI * 2

// -------------------------------------------------------------------------------------------- palette

interface LedgerFonts {
  readonly title: string
  readonly label: string
  readonly value: string
  readonly big: string
  readonly chipCode: string
  readonly small: string
  readonly stamp: string
}

/** Colours, fonts and the glow sprites drawn with them; rebuilt when the theme changes. */
interface LedgerPalette {
  readonly tones: Readonly<Record<Tone, string>>
  readonly background: string
  readonly fonts: LedgerFonts
  /** The stamp's box width: its text as this font renders it, plus padding. */
  readonly stampWidth: number
  readonly scanlines: CanvasPattern
  /** Horizontal glow band (transparent → frame → transparent) for row sweeps. */
  readonly sweep: HTMLCanvasElement
  /** Vertical glow, frame at the bottom fading to transparent at the top. */
  readonly edgeGlow: HTMLCanvasElement
}

/** Theme custom properties (theme.ts, theme.css) the ledger paints with. */
const TONE_PROPERTIES: Readonly<Record<Tone, string>> = {
  frame: '--tw-frame',
  text: '--tw-text',
  textDim: '--tw-text-dim',
  money: '--tw-money',
  warning: '--tw-warning',
  player: '--tw-player',
  enemy: '--tw-enemy',
}

function readPalette(ctx: CanvasRenderingContext2D, themed: Element): LedgerPalette {
  const style = getComputedStyle(themed)
  const property = (name: string): string => {
    const value = style.getPropertyValue(name).trim()
    if (value === '') throw new HudError(`theme property ${name} is not set where the ledger is mounted`)
    return value
  }
  const tones = {} as Record<Tone, string>
  for (const tone of Object.keys(TONE_PROPERTIES) as Tone[]) tones[tone] = property(TONE_PROPERTIES[tone])
  const mono = property('--tw-font-mono')
  const display = property('--tw-font-display')
  const fonts: LedgerFonts = {
    title: `600 12px ${display}`,
    label: `10px ${mono}`,
    value: `12px ${mono}`,
    big: `600 26px ${display}`,
    chipCode: `600 11px ${display}`,
    small: `10px ${mono}`,
    stamp: `700 10px ${display}`,
  }
  ctx.save()
  ctx.font = fonts.stamp
  ctx.letterSpacing = STAMP.letterSpacing
  const stampWidth = ctx.measureText(LEDGER_COPY.stamp).width + 2 * STAMP.padX
  ctx.restore()
  return {
    tones,
    background: property('--tw-bg'),
    fonts,
    stampWidth,
    scanlines: scanlinePattern(ctx, tones.frame),
    sweep: glowSprite(SWEEP_WIDTH_PX, 1, tones.frame, [0, HALF, 1], [0, 1, 0]),
    edgeGlow: glowSprite(1, GLOW_PX, tones.frame, [0, 1], [0, 1]),
  }
}

/**
 * A small offscreen canvas of `colour` whose opacity ramps through `alphas` at `stops` along its longer side, drawn
 * scaled instead of re-created. The ramp is an alpha mask, so the colour never darkens toward transparent the way a
 * gradient through 'transparent' (transparent black) would.
 */
function glowSprite(width: number, height: number, colour: string, stops: readonly number[], alphas: readonly number[]): HTMLCanvasElement {
  const sprite = document.createElement('canvas')
  sprite.width = width
  sprite.height = height
  const context = sprite.getContext('2d')
  if (context === null) throw new HudError('no 2D context for a ledger glow sprite')
  context.fillStyle = colour
  context.fillRect(0, 0, width, height)
  const mask = width >= height ? context.createLinearGradient(0, 0, width, 0) : context.createLinearGradient(0, 0, 0, height)
  stops.forEach((stop, i) => mask.addColorStop(stop, `rgba(0, 0, 0, ${alphas[i] as number})`))
  context.globalCompositeOperation = 'destination-in'
  context.fillStyle = mask
  context.fillRect(0, 0, width, height)
  return sprite
}

/** Horizontal hologram scanlines: one frame-coloured line every SCANLINE_PERIOD_PX, as a repeating pattern. */
function scanlinePattern(ctx: CanvasRenderingContext2D, colour: string): CanvasPattern {
  const tile = document.createElement('canvas')
  tile.width = 1
  tile.height = SCANLINE_PERIOD_PX
  const tileContext = tile.getContext('2d')
  if (tileContext === null) throw new HudError('no 2D context for the ledger scanline tile')
  tileContext.fillStyle = colour
  tileContext.fillRect(0, 0, 1, 1)
  const pattern = ctx.createPattern(tile, 'repeat')
  if (pattern === null) throw new HudError('the ledger scanline pattern could not be created')
  return pattern
}

// -------------------------------------------------------------------------------------------- painter

/** A figure's shown text, rebuilt only when the shown whole number changes (a rolling figure changes most frames). */
class FigureText {
  private shown = Number.NaN
  private text = ''

  of(value: number, format: FigureFormatter): string {
    const whole = format.whole(value)
    if (whole !== this.shown) {
      this.shown = whole
      this.text = format.text(whole)
    }
    return this.text
  }
}

const COUNT_FORMAT: FigureFormatter = { whole: Math.round, text: formatCount }

/** A chip's deploy count. */
export function formatCount(count: number): string {
  return `×${formatWhole(count)}`
}

function figureTexts(): Readonly<Record<FigureKey, FigureText>> {
  const texts = {} as Record<FigureKey, FigureText>
  for (const key of FIGURE_KEYS) texts[key] = new FigureText()
  return texts
}

export class LedgerPainter {
  private palette: LedgerPalette | null = null
  private readonly figureTexts = figureTexts()
  private readonly legendTexts = SPEND_PARTS.map(() => new FigureText())
  private readonly chipCounts = UNIT_KEYS.map(() => new FigureText())
  private readonly chipSpent = UNIT_KEYS.map(() => new FigureText())
  private readonly chipCodes: readonly string[] = UNIT_KEYS.map((key) => UNIT_PRESENTATION[key].code)

  /** The theme changed (colour-blind palette): colours, fonts and sprites are rebuilt on the next paint. */
  invalidatePalette(): void {
    this.palette = null
  }

  /**
   * Paints the sheet at ledger time `t` (∞ = settled) onto `ctx`, whose transform maps the sheet's CSS px. `themed` is
   * an element inside the themed HUD tree (its custom properties give the colours and fonts).
   */
  paint(ctx: CanvasRenderingContext2D, themed: Element, model: LedgerModel, t: number): void {
    ctx.clearRect(0, 0, LEDGER_WIDTH, LEDGER_HEIGHT)
    const sheet = model.sheet
    if (sheet.kind === 'closed') return
    const palette = this.palette ?? (this.palette = readPalette(ctx, themed))
    const openFor = sheet.kind === 'open' ? t - sheet.since : Number.POSITIVE_INFINITY
    const unfold = sheet.kind === 'open' ? unfoldAt(openFor) : sheet.from * (1 - stepAt(SPRINGS.fold, t - sheet.since))
    const height = Math.min(LEDGER_HEIGHT, LEDGER_HEIGHT * unfold)
    if (height <= 0) return

    ctx.save()
    ctx.beginPath()
    ctx.rect(0, 0, LEDGER_WIDTH, height)
    ctx.clip()
    this.paintSheet(ctx, palette, height)
    // A closing sheet shows its settled contents (openFor = ∞), fading as it folds.
    ctx.globalAlpha = Math.min(1, unfold)
    this.paintHeader(ctx, palette, openFor)
    this.paintSpend(ctx, palette, model, t, openFor)
    this.paintRows(ctx, palette, model, t, openFor)
    this.paintDeploys(ctx, palette, model, t, openFor)
    this.paintStamp(ctx, palette, model, t)
    ctx.restore()

    const scan = sheet.kind === 'closing' ? 1 : 1 - progress(openFor, SPRINGS.snap.settle, SCAN_FADE_SEC)
    if (scan > 0) this.paintScanLine(ctx, palette, height, scan)
  }

  private paintSheet(ctx: CanvasRenderingContext2D, palette: LedgerPalette, height: number): void {
    ctx.globalAlpha = ALPHA.sheet
    ctx.fillStyle = palette.background
    ctx.fillRect(0, 0, LEDGER_WIDTH, height)
    // Top glow: the edge sprite flipped, so the frame colour sits at the sheet's top edge.
    ctx.globalAlpha = ALPHA.glow
    ctx.save()
    ctx.scale(1, -1)
    ctx.drawImage(palette.edgeGlow, 0, -GLOW_PX, LEDGER_WIDTH, GLOW_PX)
    ctx.restore()
    ctx.globalAlpha = ALPHA.scanlines
    ctx.fillStyle = palette.scanlines
    ctx.fillRect(0, 0, LEDGER_WIDTH, height)
    ctx.globalAlpha = ALPHA.border
    ctx.strokeStyle = palette.tones.frame
    ctx.lineWidth = 1
    ctx.strokeRect(HALF, HALF, LEDGER_WIDTH - 1, LEDGER_HEIGHT - 1)
    ctx.globalAlpha = ALPHA.bracket
    ctx.fillStyle = palette.tones.frame
    ctx.fillRect(0, 0, BRACKET.size, BRACKET.width)
    ctx.fillRect(0, 0, BRACKET.width, BRACKET.size)
    ctx.fillRect(LEDGER_WIDTH - BRACKET.size, LEDGER_HEIGHT - BRACKET.width, BRACKET.size, BRACKET.width)
    ctx.fillRect(LEDGER_WIDTH - BRACKET.width, LEDGER_HEIGHT - BRACKET.size, BRACKET.width, BRACKET.size)
  }

  private paintHeader(ctx: CanvasRenderingContext2D, palette: LedgerPalette, openFor: number): void {
    const base = ctx.globalAlpha
    const typed = Math.min(LEDGER_COPY.title.length, Math.floor(Math.max(0, openFor - BEATS.titleTyping) * TITLE_CHARS_PER_SEC))
    ctx.font = palette.fonts.title
    ctx.letterSpacing = '2px'
    ctx.fillStyle = palette.tones.frame
    ctx.textAlign = 'left'
    ctx.fillText(TITLE_PREFIXES[typed] as string, PAD_X, LAYOUT.titleY)
    ctx.letterSpacing = '0px'

    // Close control: a thin ✕ in a box, with the key that also closes the ledger.
    const box = CLOSE_BOX
    ctx.globalAlpha = base * ALPHA.close
    ctx.strokeStyle = palette.tones.frame
    ctx.lineWidth = 1
    ctx.strokeRect(box.x + HALF, box.y + HALF, box.size - 1, box.size - 1)
    ctx.globalAlpha = base
    ctx.lineWidth = CLOSE.crossWidth
    ctx.beginPath()
    ctx.moveTo(box.x + CLOSE.crossInset, box.y + CLOSE.crossInset)
    ctx.lineTo(box.x + box.size - CLOSE.crossInset, box.y + box.size - CLOSE.crossInset)
    ctx.moveTo(box.x + box.size - CLOSE.crossInset, box.y + CLOSE.crossInset)
    ctx.lineTo(box.x + CLOSE.crossInset, box.y + box.size - CLOSE.crossInset)
    ctx.stroke()
    ctx.font = palette.fonts.small
    ctx.fillStyle = palette.tones.textDim
    ctx.textAlign = 'right'
    ctx.fillText(LEDGER_COPY.closeHint, box.x - CLOSE.hintGap, LAYOUT.titleY)
    this.paintDivider(ctx, palette, LAYOUT.headerDivider)
  }

  private paintSpend(ctx: CanvasRenderingContext2D, palette: LedgerPalette, model: LedgerModel, t: number, openFor: number): void {
    const base = ctx.globalAlpha
    const arrive = stepAt(SPRINGS.snap, openFor - BEATS.spend)
    const roll = stepAt(SPRINGS.roll, openFor - BEATS.spend)
    const shown = base * Math.min(1, arrive)
    const dx = (1 - arrive) * ROW_SLIDE_PX
    this.paintSweep(ctx, palette, model.changedAt.warSpend, t, LAYOUT.headerDivider, LAYOUT.barY - LAYOUT.headerDivider)
    ctx.globalAlpha = shown
    ctx.textAlign = 'left'
    ctx.font = palette.fonts.label
    ctx.fillStyle = palette.tones.textDim
    ctx.fillText(SPEND_LABEL, PAD_X + dx, LAYOUT.spendLabelY)
    ctx.font = palette.fonts.big
    ctx.fillStyle = palette.tones.money
    const total = model.figures.warSpend.at(t)
    ctx.fillText(this.figureTexts.warSpend.of(total * roll, FORMATTERS.money), PAD_X + dx, LAYOUT.spendValueY)

    // Composition bar: a dim track, then each part's share growing in on its own beat.
    ctx.globalAlpha = base * ALPHA.track
    ctx.fillStyle = palette.tones.frame
    ctx.fillRect(PAD_X, LAYOUT.barY, INNER_WIDTH, LAYOUT.barHeight)
    let x = PAD_X
    for (let i = 0; i < SPEND_PARTS.length; i++) {
      const part = SPEND_PARTS[i] as (typeof SPEND_PARTS)[number]
      const amount = Math.max(0, model.figures[part.figure].at(t))
      const share = total > 0 ? amount / total : 0
      const grow = Math.min(1, stepAt(SPRINGS.snap, openFor - BEATS.bar - i * BEATS.barStagger))
      ctx.globalAlpha = base
      ctx.fillStyle = palette.tones[part.tone]
      if (share > 0) ctx.fillRect(x, LAYOUT.barY, INNER_WIDTH * share * grow, LAYOUT.barHeight)
      x += INNER_WIDTH * share

      // Legend: swatch and name, the amount under it.
      const legendX = PAD_X + i * LEGEND.column
      ctx.globalAlpha = shown
      ctx.fillRect(legendX, LAYOUT.legendNameY - LEGEND.swatch, LEGEND.swatch, LEGEND.swatch)
      ctx.font = palette.fonts.small
      ctx.fillStyle = palette.tones.textDim
      ctx.fillText(part.label, legendX + LEGEND.swatch + LEGEND.textGap, LAYOUT.legendNameY)
      ctx.fillStyle = palette.tones[part.tone]
      ctx.fillText((this.legendTexts[i] as FigureText).of(amount * roll, FORMATTERS.money), legendX + LEGEND.swatch + LEGEND.textGap, LAYOUT.legendAmountY)
    }
    ctx.globalAlpha = base
    this.paintDivider(ctx, palette, LAYOUT.spendDivider)
  }

  private paintRows(ctx: CanvasRenderingContext2D, palette: LedgerPalette, model: LedgerModel, t: number, openFor: number): void {
    const base = ctx.globalAlpha
    for (let i = 0; i < ROWS.length; i++) {
      const row = ROWS[i] as RowDef
      const beat = BEATS.rows + i * BEATS.rowStagger
      const arrive = stepAt(SPRINGS.snap, openFor - beat)
      const roll = stepAt(SPRINGS.roll, openFor - beat)
      const top = LAYOUT.rowsTop + i * ROW_HEIGHT
      const y = top + ROW_BASELINE
      const dx = (1 - arrive) * ROW_SLIDE_PX
      ctx.globalAlpha = base
      this.paintSweep(ctx, palette, model.changedAt[row.figure], t, top, ROW_HEIGHT)
      ctx.globalAlpha = base * Math.min(1, arrive)
      ctx.textAlign = 'left'
      ctx.font = palette.fonts.label
      ctx.fillStyle = palette.tones.textDim
      ctx.fillText(row.label, PAD_X + dx, y)
      if (row.pips !== 'none') this.paintPips(ctx, palette, row, model, t, dx, top + ROW_HEIGHT / 2)
      ctx.textAlign = 'right'
      ctx.font = palette.fonts.value
      const pending = figurePending(row.figure, model)
      ctx.fillStyle = pending ? palette.tones.textDim : palette.tones[row.tone]
      const text = pending ? NO_CAPTURES : this.figureTexts[row.figure].of(model.figures[row.figure].at(t) * roll, FORMATTERS[FIGURE_FORMATS[row.figure]])
      ctx.fillText(text, LEDGER_WIDTH - PAD_X + dx, y)
    }
    ctx.globalAlpha = base
    this.paintDivider(ctx, palette, LAYOUT.rowsDivider)
  }

  /** One diamond per city (up to PIPS.max); the newest pops in when its row changes. */
  private paintPips(ctx: CanvasRenderingContext2D, palette: LedgerPalette, row: RowDef, model: LedgerModel, t: number, dx: number, cy: number): void {
    const count = Math.min(PIPS.max, Math.round(model.figures[row.figure].target))
    const newest = stepAt(SPRINGS.pop, t - model.changedAt[row.figure])
    ctx.fillStyle = palette.tones[row.tone]
    ctx.strokeStyle = palette.tones[row.tone]
    ctx.lineWidth = 1
    for (let i = 0; i < count; i++) {
      const radius = PIPS.radius * (i === count - 1 ? newest : 1)
      if (radius <= 0) continue
      const cx = PIPS.x + dx + i * PIPS.step
      ctx.beginPath()
      ctx.moveTo(cx, cy - radius)
      ctx.lineTo(cx + radius, cy)
      ctx.lineTo(cx, cy + radius)
      ctx.lineTo(cx - radius, cy)
      ctx.closePath()
      if (row.pips === 'solid') ctx.fill()
      else ctx.stroke()
    }
  }

  private paintDeploys(ctx: CanvasRenderingContext2D, palette: LedgerPalette, model: LedgerModel, t: number, openFor: number): void {
    const base = ctx.globalAlpha
    ctx.globalAlpha = base * Math.min(1, stepAt(SPRINGS.snap, openFor - BEATS.deploysLabel))
    ctx.textAlign = 'left'
    ctx.font = palette.fonts.label
    ctx.fillStyle = palette.tones.textDim
    ctx.fillText(DEPLOYS_LABEL, PAD_X, LAYOUT.deploysLabelY)
    for (let i = 0; i < model.deploys.length; i++) {
      const cell = model.deploys[i] as DeployCell
      const x = PAD_X + (i % DEPLOY_COLUMNS) * (CHIP_WIDTH + CHIP.gap)
      const y = LAYOUT.chipsTop + Math.floor(i / DEPLOY_COLUMNS) * (CHIP.height + CHIP.gap)
      const arrive = stepAt(SPRINGS.pop, openFor - BEATS.chips - i * BEATS.chipStagger)
      const scale = CHIP_START_SCALE + (1 - CHIP_START_SCALE) * arrive + CHIP_BUMP * (1 - stepAt(SPRINGS.pop, t - cell.poppedAt))
      const shown = base * Math.min(1, arrive)
      const active = cell.count > 0
      ctx.save()
      ctx.translate(x + CHIP_WIDTH / 2, y + CHIP.height / 2)
      ctx.scale(scale, scale)
      ctx.translate(-CHIP_WIDTH / 2, -CHIP.height / 2)
      ctx.globalAlpha = shown * (active ? ALPHA.chipBorder : ALPHA.chipBorderIdle)
      ctx.strokeStyle = active ? palette.tones.player : palette.tones.frame
      ctx.lineWidth = 1
      ctx.strokeRect(HALF, HALF, CHIP_WIDTH - 1, CHIP.height - 1)
      ctx.globalAlpha = shown * (active ? 1 : ALPHA.chipIdle)
      ctx.textAlign = 'left'
      ctx.font = palette.fonts.chipCode
      ctx.fillStyle = palette.tones.frame
      ctx.fillText(this.chipCodes[i] as string, CHIP.inset, CHIP.codeY)
      ctx.textAlign = 'right'
      ctx.font = palette.fonts.value
      ctx.fillStyle = palette.tones.text
      ctx.fillText((this.chipCounts[i] as FigureText).of(cell.count, COUNT_FORMAT), CHIP_WIDTH - CHIP.inset, CHIP.codeY)
      ctx.textAlign = 'left'
      ctx.font = palette.fonts.small
      ctx.fillStyle = palette.tones.money
      ctx.fillText((this.chipSpent[i] as FigureText).of(cell.spent.at(t), FORMATTERS.money), CHIP.inset, CHIP.spentY)
      ctx.restore()

      // "+1" floats up off a chip that just deployed.
      const floated = progress(t, cell.poppedAt, FLOATER_SEC)
      if (floated < 1) {
        ctx.globalAlpha = base * (1 - floated)
        ctx.textAlign = 'right'
        ctx.font = palette.fonts.chipCode
        ctx.fillStyle = palette.tones.player
        ctx.fillText(PLUS_ONE, x + CHIP_WIDTH - CHIP.inset, y - FLOATER_RISE_PX * floated)
      }
    }
    ctx.globalAlpha = base
  }

  /** The PAID stamp slams down (from big and faint to its size, with a bounce) and throws off a thin ink ring. */
  private paintStamp(ctx: CanvasRenderingContext2D, palette: LedgerPalette, model: LedgerModel, t: number): void {
    const since = t - model.stampAt
    if (!(since > 0)) return
    const base = ctx.globalAlpha
    const width = palette.stampWidth
    ctx.save()
    ctx.translate(STAMP.right - width / 2, STAMP.y)
    ctx.rotate(STAMP.angle)
    const ring = progress(since, 0, STAMP_RING_SEC)
    if (ring < 1) {
      const radius = STAMP.ringFrom + (STAMP.ringTo - STAMP.ringFrom) * ring
      ctx.globalAlpha = base * ALPHA.stampRing * (1 - ring)
      ctx.strokeStyle = palette.tones.warning
      ctx.lineWidth = 1
      ctx.beginPath()
      ctx.ellipse(0, 0, radius, radius * HALF, 0, 0, FULL_TURN)
      ctx.stroke()
    }
    const scale = STAMP_START_SCALE - (STAMP_START_SCALE - 1) * stepAt(SPRINGS.pop, since)
    ctx.scale(scale, scale)
    ctx.globalAlpha = base * ALPHA.stamp * progress(since, 0, STAMP_FADE_IN_SEC)
    ctx.strokeStyle = palette.tones.warning
    ctx.lineWidth = 2
    ctx.strokeRect(-width / 2, -STAMP.height / 2, width, STAMP.height)
    ctx.font = palette.fonts.stamp
    ctx.letterSpacing = STAMP.letterSpacing
    ctx.fillStyle = palette.tones.warning
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText(LEDGER_COPY.stamp, 0, 1)
    ctx.textBaseline = 'alphabetic'
    ctx.letterSpacing = '0px'
    ctx.restore()
    ctx.globalAlpha = base
  }

  /** A soft highlight sweeping once across a band that just changed (dim and a single pass: never a flash). */
  private paintSweep(ctx: CanvasRenderingContext2D, palette: LedgerPalette, changedAt: number, t: number, top: number, height: number): void {
    const swept = progress(t, changedAt, SWEEP_SEC)
    if (swept >= 1) return
    const base = ctx.globalAlpha
    ctx.globalAlpha = base * ALPHA.sweep * (1 - swept)
    ctx.drawImage(palette.sweep, -SWEEP_WIDTH_PX + (LEDGER_WIDTH + SWEEP_WIDTH_PX) * swept, top, SWEEP_WIDTH_PX, height)
    ctx.globalAlpha = base
  }

  private paintDivider(ctx: CanvasRenderingContext2D, palette: LedgerPalette, y: number): void {
    const base = ctx.globalAlpha
    ctx.globalAlpha = base * ALPHA.divider
    ctx.fillStyle = palette.tones.frame
    ctx.fillRect(PAD_X, y, INNER_WIDTH, 1)
    ctx.globalAlpha = base
  }

  /** The bright line riding the sheet's moving edge, with a short glow above it. */
  private paintScanLine(ctx: CanvasRenderingContext2D, palette: LedgerPalette, height: number, strength: number): void {
    const glow = Math.min(height, GLOW_PX / 2)
    ctx.globalAlpha = ALPHA.glow * strength
    ctx.drawImage(palette.edgeGlow, 0, height - glow, LEDGER_WIDTH, glow)
    ctx.globalAlpha = ALPHA.scanLine * strength
    ctx.fillStyle = palette.tones.frame
    ctx.fillRect(0, height - 2, LEDGER_WIDTH, 2)
    ctx.globalAlpha = 1
  }
}
